import { useState, useMemo, useRef } from "react";
import usePageLoader from '../../hooks/usePageLoader';
import { ShimmerStatCard, ShimmerBox, ShimmerTable } from '../../components/common/Shimmer';
import {
  FiUpload, FiDownload, FiTrash2, FiRefreshCw,
  FiSearch, FiX, FiZap, FiFile,
} from "react-icons/fi";
import StatusBadge from "../../components/common/StatusBadge";
import Pagination from "../../components/common/Pagination";
import toast from "react-hot-toast";

const INITIAL_FILES = [
  { id: 1, name: "NexaGPT Product Overview.pdf", size: "444 KB",  sizeBytes: 454656,  type: "PDF",  status: "Trained",  uploadedOn: "6 days ago", trainedOn: "6 days ago" },
  { id: 2, name: "support-transcript.txt",        size: "7.7 kB",  sizeBytes: 7884,    type: "TXT",  status: "Trained",  uploadedOn: "2 days ago", trainedOn: "2 days ago" },
  { id: 3, name: "leads-export.csv",              size: "21 kB",   sizeBytes: 21504,   type: "CSV",  status: "Pending",  uploadedOn: "2 days ago", trainedOn: "—" },
  { id: 4, name: "onboarding-guide.pdf",          size: "1.2 MB",  sizeBytes: 1258291, type: "PDF",  status: "Trained",  uploadedOn: "1 day ago",  trainedOn: "1 day ago" },
  { id: 5, name: "product-notes.pdf",             size: "2.1 MB",  sizeBytes: 2202009, type: "PDF",  status: "Trained",  uploadedOn: "3 days ago", trainedOn: "3 days ago" },
  { id: 6, name: "meeting-notes.txt",             size: "4.2 kB",  sizeBytes: 4300,    type: "TXT",  status: "Pending",  uploadedOn: "1 hour ago", trainedOn: "—" },
  { id: 7, name: "api-reference.docx",            size: "318 kB",  sizeBytes: 325632,  type: "DOCX", status: "Failed",   uploadedOn: "4 days ago", trainedOn: "—" },
];

const TYPE_ICONS  = { PDF: "📄", TXT: "📝", CSV: "📊", DOCX: "📃" };
const TYPE_COLORS = { PDF: "bg-red-50 text-red-600", TXT: "bg-blue-50 text-blue-600", CSV: "bg-green-50 text-green-600", DOCX: "bg-indigo-50 text-indigo-600" };
const PAGE_SIZE   = 8;

function fmtSize(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${bytes} B`;
}

function ContentPageSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-5 space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <ShimmerStatCard key={i} />)}
      </div>
      <div className="flex items-center gap-3">
        <ShimmerBox h="h-9" w="w-56" rounded="rounded-lg" />
        <div className="flex gap-2">
          {[...Array(4)].map((_, i) => <ShimmerBox key={i} h="h-8" w="w-20" rounded="rounded-lg" />)}
        </div>
      </div>
      <ShimmerTable rows={8} cols={6} />
    </div>
  );
}

export default function Files() {
  const [files, setFiles] = useState(INITIAL_FILES);
  const loading = usePageLoader();
  const [selectedIds, setSelectedIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  /* ── Derived ── */
  const counts = useMemo(() => ({
    total:   files.length,
    trained: files.filter(f => f.status === "Trained").length,
    pending: files.filter(f => f.status === "Pending").length,
    failed:  files.filter(f => f.status === "Failed").length,
    size:    fmtSize(files.reduce((s, f) => s + f.sizeBytes, 0)),
  }), [files]);

  const filtered = useMemo(() => files.filter(f => {
    const okStatus = statusFilter === "all" || f.status === statusFilter;
    const okType   = typeFilter === "ALL" || f.type === typeFilter;
    const okSearch = !search || f.name.toLowerCase().includes(search.toLowerCase());
    return okStatus && okType && okSearch;
  }), [files, statusFilter, typeFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageIds    = paginated.map(f => f.id);
  const allPageSel = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));

  const changeStatus = (s) => { setStatusFilter(s); setPage(1); setSelectedIds([]); };
  const changeSearch = (s) => { setSearch(s); setPage(1); };
  const toggleSel    = (id) => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAllSel = () => {
    if (allPageSel) setSelectedIds(p => p.filter(id => !pageIds.includes(id)));
    else setSelectedIds(p => [...new Set([...p, ...pageIds])]);
  };

  const simulateRetrain = (ids, cb) => {
    setFiles(p => p.map(f => ids.includes(f.id) ? { ...f, status: "Processing" } : f));
    setTimeout(() => {
      setFiles(p => p.map(f => ids.includes(f.id) ? { ...f, status: "Trained", trainedOn: "Just now" } : f));
      cb?.();
    }, 2000);
  };

  /* ── Upload handler ── */
  const processUpload = (fileList) => {
    const uploaded = Array.from(fileList || []);
    if (!uploaded.length) return;
    const newFiles = uploaded.map(f => ({
      id: Date.now() + Math.random(),
      name: f.name,
      size: fmtSize(f.size),
      sizeBytes: f.size,
      type: f.name.split(".").pop().toUpperCase(),
      status: "Pending",
      uploadedOn: "Just now",
      trainedOn: "—",
    }));
    setFiles(p => [...newFiles, ...p]);
    setPage(1);
    toast.success(`${uploaded.length} file${uploaded.length > 1 ? "s" : ""} uploaded and queued for training.`);
  };

  const handleFileInput = (e) => { processUpload(e.target.files); e.target.value = ""; };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    processUpload(e.dataTransfer.files);
  };

  /* ── Handlers ── */
  const handleDelete = (id) => {
    setFiles(p => p.filter(f => f.id !== id));
    setSelectedIds(p => p.filter(x => x !== id));
    toast.success("File removed.");
  };

  const handleRetrain = (id) => simulateRetrain([id], () => toast.success("File retrained."));

  const handleBulkDelete = () => {
    const n = selectedIds.length;
    setFiles(p => p.filter(f => !selectedIds.includes(f.id)));
    setSelectedIds([]); toast.success(`${n} file${n > 1 ? "s" : ""} deleted.`);
  };

  const handleBulkRetrain = () => {
    const ids = [...selectedIds];
    simulateRetrain(ids, () => { setSelectedIds([]); toast.success(`${ids.length} file${ids.length > 1 ? "s" : ""} retrained.`); });
  };

  const handleTrainAllPending = () => {
    const ids = files.filter(f => f.status === "Pending").map(f => f.id);
    if (!ids.length) return;
    simulateRetrain(ids, () => toast.success(`${ids.length} pending file${ids.length > 1 ? "s" : ""} trained.`));
  };

  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* ── Sticky header ── */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Files</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {counts.total} files · {counts.trained} trained · {counts.size} total
          </p>
        </div>
        <div className="flex items-center gap-2">
          {counts.pending > 0 && (
            <button
              onClick={handleTrainAllPending}
              className="flex items-center gap-2 text-sm border border-primary text-primary px-4 py-2 rounded-lg hover:bg-primary/5 transition"
            >
              <FiZap size={14} /> Train {counts.pending} Pending
            </button>
          )}
          <input ref={fileRef} type="file" hidden multiple accept=".pdf,.txt,.csv,.docx" onChange={handleFileInput} />
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm hover:opacity-90 transition"
          >
            <FiUpload size={14} /> Upload Files
          </button>
        </div>
      </div>

      {loading ? <ContentPageSkeleton /> : <div
        className={`flex-1 overflow-y-auto bg-gray-50 ${dragging ? "ring-2 ring-primary ring-inset" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        {dragging && (
          <div className="fixed inset-0 z-30 pointer-events-none flex items-center justify-center">
            <div className="bg-primary/10 border-2 border-dashed border-primary rounded-2xl px-16 py-10 text-center">
              <FiUpload size={36} className="mx-auto text-primary mb-2" />
              <p className="text-primary font-semibold text-lg">Drop files to upload</p>
            </div>
          </div>
        )}

        <div className="p-6 space-y-4">

          {/* KPI cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard label="Total Files"  value={counts.total}   color="text-gray-900" />
            <KpiCard label="Trained"      value={counts.trained} color="text-green-600" dot="bg-green-500" />
            <KpiCard label="Pending / Failed" value={`${counts.pending} / ${counts.failed}`} color="text-yellow-600" dot="bg-yellow-500" />
            <KpiCard label="Total Size"   value={counts.size}    color="text-primary" />
          </div>

          {/* Search + filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text" value={search}
                onChange={e => changeSearch(e.target.value)}
                placeholder="Search files…"
                className="w-full pl-9 pr-8 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
              />
              {search && (
                <button onClick={() => changeSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <FiX size={13} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={typeFilter}
                onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
              >
                {["ALL", "PDF", "TXT", "CSV", "DOCX"].map(t => (
                  <option key={t} value={t}>{t === "ALL" ? "All Types" : t}</option>
                ))}
              </select>
              <div className="flex items-center border rounded-lg overflow-hidden text-xs font-medium">
                {["all", "Trained", "Pending", "Failed"].map(f => (
                  <button
                    key={f}
                    onClick={() => changeStatus(f)}
                    className={`px-3 py-1.5 transition ${statusFilter === f ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-50"}`}
                  >
                    {f === "all" ? "All" : f}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Bulk action bar */}
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-2.5">
              <span className="text-sm font-medium text-primary">{selectedIds.length} selected</span>
              <div className="flex gap-2 ml-auto">
                <button onClick={handleBulkRetrain} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 bg-white text-gray-700 rounded-lg hover:bg-gray-50">
                  <FiRefreshCw size={12} /> Retrain Selected
                </button>
                <button onClick={handleBulkDelete} className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200">
                  <FiTrash2 size={12} /> Delete Selected
                </button>
                <button onClick={() => setSelectedIds([])} className="text-gray-400 hover:text-gray-600 p-1"><FiX size={14} /></button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-white border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                  <tr>
                    <th className="w-10 pl-4 py-3">
                      <input type="checkbox" checked={allPageSel} onChange={toggleAllSel} className="accent-primary" />
                    </th>
                    <th className="px-3 py-3 text-left font-semibold">File</th>
                    <th className="py-3 text-left font-semibold">Type</th>
                    <th className="py-3 text-left font-semibold">Status</th>
                    <th className="py-3 text-left font-semibold">Uploaded</th>
                    <th className="py-3 text-left font-semibold">Trained</th>
                    <th className="py-3 pr-4 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-16">
                        <FiFile size={30} className="mx-auto mb-2 text-gray-300" />
                        <p className="text-sm text-gray-500">
                          {search || statusFilter !== "all" || typeFilter !== "ALL" ? "No files match your filters." : "No files uploaded yet."}
                        </p>
                        {!search && statusFilter === "all" && typeFilter === "ALL" && (
                          <button onClick={() => fileRef.current?.click()} className="mt-3 text-sm text-primary hover:underline">
                            Upload your first file
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : paginated.map(file => (
                    <tr key={file.id} className={`hover:bg-gray-50 transition ${selectedIds.includes(file.id) ? "bg-primary/5" : ""}`}>
                      <td className="pl-4 py-3">
                        <input type="checkbox" checked={selectedIds.includes(file.id)} onChange={() => toggleSel(file.id)} className="accent-primary" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5">
                          <span className="text-lg shrink-0">{TYPE_ICONS[file.type] || "📁"}</span>
                          <div>
                            <p className="font-medium text-gray-800 max-w-xs truncate" title={file.name}>{file.name}</p>
                            <p className="text-xs text-gray-400">{file.size}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-3">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${TYPE_COLORS[file.type] || "bg-gray-100 text-gray-600"}`}>
                          {file.type}
                        </span>
                      </td>
                      <td className="py-3 pr-3"><StatusBadge status={file.status} /></td>
                      <td className="py-3 pr-3 text-gray-500 text-xs whitespace-nowrap">{file.uploadedOn}</td>
                      <td className="py-3 pr-3 text-gray-500 text-xs whitespace-nowrap">{file.trainedOn}</td>
                      <td className="py-3 pr-4">
                        <div className="flex justify-end gap-2">
                          {file.status === "Processing" ? (
                            <span className="flex items-center gap-1.5 text-xs text-blue-600 px-3 py-1.5">
                              <span className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                              Training…
                            </span>
                          ) : (
                            <>
                              <button onClick={() => handleRetrain(file.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition">
                                <FiRefreshCw size={12} /> Retrain
                              </button>
                              <button className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition">
                                <FiDownload size={12} /> Download
                              </button>
                              <button onClick={() => handleDelete(file.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition">
                                <FiTrash2 size={12} /> Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination footer */}
          {filtered.length > PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm text-gray-500">
              <p>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} files</p>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}

        </div>
      </div>}
    </div>
  );
}

function KpiCard({ label, value, color, dot }) {
  return (
    <div className="bg-white border rounded-xl px-4 py-3">
      <div className="flex items-center gap-1.5 mb-1">
        {dot && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />}
        <p className="text-xs text-gray-500 font-medium truncate">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}
